<template>
  <v-row :dense="!editMode">
    <!-- Plan Type -->
    <v-col :cols="editMode ? 12 : 6" sm="4">
      <template v-if="!editMode">
        <div class="text-caption text--secondary">Plan Type</div>
        <div>
          {{ plan.planType || "—" }}
          <span v-if="ageCategory" class="text-caption text--secondary"
            >({{ ageCategory }})</span
          >
        </div>
      </template>
      <validation-provider v-else rules="required" v-slot="{ errors }">
        <v-autocomplete
          v-model="plan.planTypeId"
          label="Plan Type"
          :items="groupedPlanTypes"
          item-text="description"
          item-value="planTypeId"
          :hint="plan.planTypeId ? `(${ageCategory})` : ''"
          persistent-hint
          :error-messages="errors"
          clearable
        ></v-autocomplete>
      </validation-provider>
    </v-col>

    <!-- Language -->
    <v-col :cols="editMode ? 12 : 6" sm="4">
      <template v-if="!editMode">
        <div class="text-caption text--secondary">Language</div>
        <div>{{ plan.language || "—" }}</div>
      </template>
      <validation-provider v-else rules="required" v-slot="{ errors }">
        <v-autocomplete
          v-model="plan.languageId"
          label="Language"
          :items="languages"
          item-text="name"
          item-value="languageId"
          :error-messages="errors"
          clearable
        ></v-autocomplete>
      </validation-provider>
    </v-col>

    <!-- 10/12 Month (Preschool / SchoolAge only) -->
    <v-col :cols="editMode ? 12 : 6" sm="4">
      <template v-if="isSemesterBasedPlan">
        <template v-if="!editMode">
          <div class="text-caption text--secondary">10/12 Month</div>
          <div>{{ tenTwelveMonthLabel }}</div>
        </template>
        <validation-provider
          v-else
          :rules="{ required: true }"
          v-slot="{ errors }"
        >
          <v-radio-group
            v-model="plan.isSummerSemesterIncluded"
            label="10/12 Month"
            row
            class="mx-1 mt-1"
            :error-messages="errors"
          >
            <v-radio label="10 Month" :value="false"></v-radio>
            <v-radio label="12 Month" :value="true"></v-radio>
          </v-radio-group>
        </validation-provider>
      </template>
    </v-col>

    <!-- IEP Meeting Date -->
    <v-col :cols="editMode ? 12 : 6" sm="4">
      <template v-if="!editMode">
        <div class="text-caption text--secondary">IEP Meeting Date</div>
        <div>{{ plan.formattedMeetingDate || "—" }}</div>
      </template>
      <div v-else class="pt-2">
        <date-picker
          :field="{ label: 'IEP Meeting Date' }"
          :value="plan.planMeetingDate"
          :validations="{ required: true }"
          @fieldChange="plan.planMeetingDate = $event"
        ></date-picker>
      </div>
    </v-col>

    <!-- IEP Plan Date -->
    <v-col :cols="editMode ? 12 : 6" sm="4">
      <template v-if="!editMode">
        <div class="text-caption text--secondary">IEP Plan Date</div>
        <div>{{ plan.formattedStartDate || "—" }}</div>
      </template>
      <div v-else class="pt-2">
        <date-picker
          :field="{ label: 'IEP Plan Date' }"
          :value="plan.planStartDate"
          :validations="{ required: true }"
          @fieldChange="plan.planStartDate = $event"
        ></date-picker>
      </div>
    </v-col>

    <!-- Plan Closure Date -->
    <v-col :cols="editMode ? 12 : 6" sm="4">
      <template v-if="!editMode">
        <div class="text-caption text--secondary">Closure Date</div>
        <div>{{ plan.formattedEndDate || "—" }}</div>
      </template>
      <div v-else class="pt-2">
        <date-picker
          :field="{ label: 'Plan Closure Date' }"
          :value="plan.closureDate"
          :validations="{ minDate: plan.planStartDate }"
          @fieldChange="plan.closureDate = $event"
          :readonly="isNewPlan"
        ></date-picker>
      </div>
    </v-col>
  </v-row>
</template>

<script>
// The plan header fields, in one place. In read mode each field is label/value
// text; in edit mode it becomes the matching form control. `plan` is whatever
// the caller hands it: the store plan in read mode, or plan-editor's editable copy
// in edit mode (edit mutates it in place, so the caller must pass a copy).
export default {
  props: ["plan", "editMode", "isNewPlan"],
  computed: {
    planTypes() {
      return this.$store.state.optionsModule.planTypes;
    },
    groupedPlanTypes() {
      // group plan types by age category; "Unassigned" stays disabled — it is a
      // system-only placeholder assigned when a transmittal has no matching plan.
      return this.planTypes.reduce((group, planType) => {
        let match = group.find(
          (item) => item.ageCategory === planType.ageCategory
        );
        if (!match) {
          group.push({ header: planType.ageCategory });
        }
        // "Unassigned" is a system-only placeholder.
        group.push({
          ...planType,
          disabled: planType.description == "Unassigned",
        });
        return group;
      }, []);
    },
    ageCategory() {
      return this.plan.ageCategory || "";
    },
    isSemesterBasedPlan() {
      return ["Preschool", "SchoolAge"].includes(this.ageCategory);
    },
    languages() {
      return this.$store.state.optionsModule.languages || [];
    },
    tenTwelveMonthLabel() {
      if (this.plan.isSummerSemesterIncluded === true) return "12 Month";
      if (this.plan.isSummerSemesterIncluded === false) return "10 Month";
      return "—";
    },
  },
};
</script>
